import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import {
  HomeInsuranceFormData,
  HomeInsurancePersonalInfo,
  HomeInsurancePropertyInfo,
  HomeInsuranceCoverageInfo,
  PersonType,
  PolicyholderRole,
  IdType,
  MaritalStatus,
  PropertyType,
  PropertyUse,
  CoverageType,
  NATIONALITIES,
  DIAL_CODES,
} from '../../types/forms';

const initialPersonalInfo: HomeInsurancePersonalInfo = {
  personType: 'individual',
  policyholderRole: undefined,
  fullName: '',
  idType: undefined,
  idFrontFile: undefined,
  idBackFile: undefined,
  maritalStatus: undefined,
  nationality: '',
  occupation: '',
  email: '',
  dialCode: '+52',
  phoneNumber: '',
};

const initialPropertyInfo: HomeInsurancePropertyInfo = {
  propertyType: 'house',
  address: '',
  proofOfAddressFile: undefined,
  nearSea: null,
  numberOfFloors: null,
  numberOfRooms: null,
  hasBasement: null,
  propertyUse: null,
  wallsConcreteBlocks: null,
  wallsDetails: '',
  ceilingsConcrete: null,
  ceilingsDetails: '',
};

const initialCoverageInfo: HomeInsuranceCoverageInfo = {
  constructionSquareMeters: null,
  coverageType: null,
  currency: 'MXN',
};

const HomeInsuranceFormPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const prefix = isEnglish ? '/en' : '';

  const [formData, setFormData] = useLocalStorage<HomeInsuranceFormData>(
    'home-insurance-form',
    {
      personalInfo: initialPersonalInfo,
      propertyInfo: initialPropertyInfo,
      coverageInfo: initialCoverageInfo,
      currentStep: 1,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  );

  const [currentStep, setCurrentStep] = useState(formData.currentStep);

  // File refs for uploads
  const idFrontRef = useRef<HTMLInputElement>(null);
  const idBackRef = useRef<HTMLInputElement>(null);
  const proofOfAddressRef = useRef<HTMLInputElement>(null);

  const updatePersonalInfo = (updates: Partial<HomeInsurancePersonalInfo>) => {
    setFormData({
      ...formData,
      personalInfo: { ...formData.personalInfo, ...updates },
      updatedAt: new Date().toISOString(),
    });
  };

  const updatePropertyInfo = (updates: Partial<HomeInsurancePropertyInfo>) => {
    setFormData({
      ...formData,
      propertyInfo: { ...formData.propertyInfo, ...updates },
      updatedAt: new Date().toISOString(),
    });
  };

  const updateCoverageInfo = (updates: Partial<HomeInsuranceCoverageInfo>) => {
    setFormData({
      ...formData,
      coverageInfo: { ...formData.coverageInfo, ...updates },
      updatedAt: new Date().toISOString(),
    });
  };

  const handleNext = () => {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setFormData({
      ...formData,
      currentStep: nextStep,
      updatedAt: new Date().toISOString(),
    });
  };

  const handlePrevious = () => {
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    setFormData({
      ...formData,
      currentStep: prevStep,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleSubmit = () => {
    setFormData({
      ...formData,
      status: 'submitted',
      updatedAt: new Date().toISOString(),
    });
    alert(isEnglish ? 'Form submitted successfully!' : 'Formulario enviado exitosamente!');
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'idFrontFile' | 'idBackFile' | 'proofOfAddressFile'
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (field === 'proofOfAddressFile') {
        updatePropertyInfo({ [field]: file.name });
      } else {
        updatePersonalInfo({ [field]: file.name });
      }
    }
  };

  // Validation for Step 1
  const isStep1Valid = () => {
    const p = formData.personalInfo;
    if (p.personType === 'individual') {
      const hasId = p.idType && p.idFrontFile && (p.idType === 'passport' || p.idBackFile);
      return (
        p.policyholderRole &&
        p.fullName.trim() !== '' &&
        hasId &&
        p.maritalStatus &&
        p.nationality !== '' &&
        p.occupation.trim() !== '' &&
        p.email.trim() !== '' &&
        p.phoneNumber.trim() !== ''
      );
    }
    // Entity validation - simplified for now
    return p.fullName.trim() !== '' && p.email.trim() !== '' && p.phoneNumber.trim() !== '';
  };

  // Validation for Step 2
  const isStep2Valid = () => {
    const p = formData.propertyInfo;
    const wallsValid = p.wallsConcreteBlocks === true || (p.wallsConcreteBlocks === false && p.wallsDetails.trim() !== '');
    const ceilingsValid = p.ceilingsConcrete === true || (p.ceilingsConcrete === false && p.ceilingsDetails.trim() !== '');

    return (
      p.propertyType &&
      p.address.trim() !== '' &&
      p.nearSea !== null &&
      p.numberOfFloors !== null &&
      p.numberOfRooms !== null &&
      p.hasBasement !== null &&
      p.propertyUse !== null &&
      p.wallsConcreteBlocks !== null &&
      wallsValid &&
      p.ceilingsConcrete !== null &&
      ceilingsValid
    );
  };

  // Validation for Step 3
  const isStep3Valid = () => {
    const c = formData.coverageInfo;
    return c.coverageType !== null && c.currency !== '';
  };

  const steps = [
    { number: 1, title: isEnglish ? 'Personal Information' : 'Información Personal' },
    { number: 2, title: isEnglish ? 'Property Information' : 'Información de la Propiedad' },
    { number: 3, title: isEnglish ? 'Values & Coverage' : 'Valores y Cobertura' },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-700 to-primary-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <Link
              to={isEnglish ? `${prefix}/wip/forms` : `${prefix}/wip/formularios`}
              className="inline-flex items-center text-primary-200 hover:text-white mb-4 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {isEnglish ? 'Back to Forms' : 'Volver a Formularios'}
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              {isEnglish ? 'Home Insurance Application' : 'Solicitud de Seguro de Hogar'}
            </h1>
            <p className="text-primary-100">
              {isEnglish ? 'Complete all steps to submit your application' : 'Complete todos los pasos para enviar su solicitud'}
            </p>
          </div>
        </div>
      </section>

      {/* Progress Bar */}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      step.number <= currentStep
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step.number < currentStep ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      step.number
                    )}
                  </div>
                  <span className={`ml-3 hidden sm:block text-sm font-medium ${
                    step.number <= currentStep ? 'text-primary-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    step.number < currentStep ? 'bg-primary-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {isEnglish ? 'Personal Information' : 'Información Personal'}
                </h2>

                {/* Person Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Are you filing as:' : '¿Va a solicitar como:'} *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => updatePersonalInfo({ personType: 'individual' })}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        formData.personalInfo.personType === 'individual'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-medium">{isEnglish ? 'Individual' : 'Persona Física'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePersonalInfo({ personType: 'entity' })}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        formData.personalInfo.personType === 'entity'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-medium">{isEnglish ? 'Entity' : 'Persona Moral'}</span>
                    </button>
                  </div>
                </div>

                {formData.personalInfo.personType === 'individual' && (
                  <>
                    {/* Policyholder Role */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {isEnglish ? 'Are you the:' : '¿Usted es:'} *
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => updatePersonalInfo({ policyholderRole: 'landlord' })}
                          className={`p-4 border-2 rounded-lg text-center transition-all ${
                            formData.personalInfo.policyholderRole === 'landlord'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="font-medium">{isEnglish ? 'Landlord (Owner)' : 'Propietario'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => updatePersonalInfo({ policyholderRole: 'tenant' })}
                          className={`p-4 border-2 rounded-lg text-center transition-all ${
                            formData.personalInfo.policyholderRole === 'tenant'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="font-medium">{isEnglish ? 'Tenant' : 'Inquilino'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Full Name' : 'Nombre Completo'} *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.fullName}
                        onChange={(e) => updatePersonalInfo({ fullName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={isEnglish ? 'Enter your full name' : 'Ingrese su nombre completo'}
                      />
                    </div>

                    {/* ID Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {isEnglish ? 'ID Document Type' : 'Tipo de Identificación'} *
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(['ine', 'fm2', 'fm3', 'passport'] as IdType[]).map((idType) => (
                          <button
                            key={idType}
                            type="button"
                            onClick={() => updatePersonalInfo({ idType, idFrontFile: undefined, idBackFile: undefined })}
                            className={`p-3 border-2 rounded-lg text-center transition-all ${
                              formData.personalInfo.idType === idType
                                ? 'border-primary-600 bg-primary-50 text-primary-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <span className="font-medium text-sm">
                              {idType === 'ine' ? 'INE' :
                               idType === 'fm2' ? 'FM2' :
                               idType === 'fm3' ? 'FM3' :
                               isEnglish ? 'Passport' : 'Pasaporte'}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ID Upload */}
                    {formData.personalInfo.idType && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Front */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {isEnglish ? 'ID Front' : 'Identificación (Frente)'} *
                          </label>
                          <div
                            onClick={() => idFrontRef.current?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                          >
                            <input
                              ref={idFrontRef}
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => handleFileUpload(e, 'idFrontFile')}
                            />
                            {formData.personalInfo.idFrontFile ? (
                              <div className="text-primary-600">
                                <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm">{formData.personalInfo.idFrontFile}</span>
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <span className="text-sm">{isEnglish ? 'Click to upload' : 'Clic para subir'}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Back (not for passport) */}
                        {formData.personalInfo.idType !== 'passport' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {isEnglish ? 'ID Back' : 'Identificación (Reverso)'} *
                            </label>
                            <div
                              onClick={() => idBackRef.current?.click()}
                              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                            >
                              <input
                                ref={idBackRef}
                                type="file"
                                accept="image/*,.pdf"
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, 'idBackFile')}
                              />
                              {formData.personalInfo.idBackFile ? (
                                <div className="text-primary-600">
                                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="text-sm">{formData.personalInfo.idBackFile}</span>
                                </div>
                              ) : (
                                <div className="text-gray-500">
                                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                  <span className="text-sm">{isEnglish ? 'Click to upload' : 'Clic para subir'}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Marital Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Marital Status' : 'Estado Civil'} *
                      </label>
                      <select
                        value={formData.personalInfo.maritalStatus || ''}
                        onChange={(e) => updatePersonalInfo({ maritalStatus: e.target.value as MaritalStatus })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">{isEnglish ? 'Select...' : 'Seleccionar...'}</option>
                        <option value="single">{isEnglish ? 'Single' : 'Soltero(a)'}</option>
                        <option value="married">{isEnglish ? 'Married' : 'Casado(a)'}</option>
                        <option value="widowed">{isEnglish ? 'Widowed' : 'Viudo(a)'}</option>
                        <option value="divorced">{isEnglish ? 'Divorced' : 'Divorciado(a)'}</option>
                        <option value="separated">{isEnglish ? 'Separated' : 'Separado(a)'}</option>
                        <option value="registeredPartnership">{isEnglish ? 'Registered Partnership' : 'Unión Libre'}</option>
                      </select>
                    </div>

                    {/* Nationality */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Nationality' : 'Nacionalidad'} *
                      </label>
                      <select
                        value={formData.personalInfo.nationality}
                        onChange={(e) => updatePersonalInfo({ nationality: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">{isEnglish ? 'Select...' : 'Seleccionar...'}</option>
                        {NATIONALITIES.map((nationality) => (
                          <option key={nationality} value={nationality}>{nationality}</option>
                        ))}
                      </select>
                    </div>

                    {/* Occupation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Occupation' : 'Ocupación'} *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.occupation}
                        onChange={(e) => updatePersonalInfo({ occupation: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={isEnglish ? 'Enter your occupation' : 'Ingrese su ocupación'}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Email' : 'Correo Electrónico'} *
                      </label>
                      <input
                        type="email"
                        value={formData.personalInfo.email}
                        onChange={(e) => updatePersonalInfo({ email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={isEnglish ? 'example@email.com' : 'ejemplo@correo.com'}
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Phone Number' : 'Número de Teléfono'} *
                      </label>
                      <div className="flex gap-3">
                        <select
                          value={formData.personalInfo.dialCode}
                          onChange={(e) => updatePersonalInfo({ dialCode: e.target.value })}
                          className="w-32 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          {DIAL_CODES.map((dc) => (
                            <option key={dc.code} value={dc.code}>{dc.code} {dc.country}</option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          value={formData.personalInfo.phoneNumber}
                          onChange={(e) => updatePersonalInfo({ phoneNumber: e.target.value })}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="55 1234 5678"
                        />
                      </div>
                    </div>
                  </>
                )}

                {formData.personalInfo.personType === 'entity' && (
                  <>
                    {/* Entity Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Company/Entity Name' : 'Nombre de la Empresa/Entidad'} *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.fullName}
                        onChange={(e) => updatePersonalInfo({ fullName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={isEnglish ? 'Enter company name' : 'Ingrese el nombre de la empresa'}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Contact Email' : 'Correo de Contacto'} *
                      </label>
                      <input
                        type="email"
                        value={formData.personalInfo.email}
                        onChange={(e) => updatePersonalInfo({ email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={isEnglish ? 'contact@company.com' : 'contacto@empresa.com'}
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Contact Phone' : 'Teléfono de Contacto'} *
                      </label>
                      <div className="flex gap-3">
                        <select
                          value={formData.personalInfo.dialCode}
                          onChange={(e) => updatePersonalInfo({ dialCode: e.target.value })}
                          className="w-32 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          {DIAL_CODES.map((dc) => (
                            <option key={dc.code} value={dc.code}>{dc.code} {dc.country}</option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          value={formData.personalInfo.phoneNumber}
                          onChange={(e) => updatePersonalInfo({ phoneNumber: e.target.value })}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="55 1234 5678"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Navigation */}
                <div className="flex justify-end pt-6 border-t">
                  <button
                    onClick={handleNext}
                    disabled={!isStep1Valid()}
                    className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isEnglish ? 'Continue' : 'Continuar'}
                    <svg className="w-5 h-5 inline-block ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Property Information */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {isEnglish ? 'Property Information' : 'Información de la Propiedad'}
                </h2>

                {/* Property Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Property Type' : 'Tipo de Propiedad'} *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ propertyType: 'house' })}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.propertyType === 'house'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <span className="font-medium">{isEnglish ? 'House' : 'Casa'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ propertyType: 'condominium' })}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.propertyType === 'condominium'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-medium">{isEnglish ? 'Condominium' : 'Condominio'}</span>
                    </button>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isEnglish ? 'Property Address' : 'Dirección de la Propiedad'} *
                  </label>
                  <textarea
                    value={formData.propertyInfo.address}
                    onChange={(e) => updatePropertyInfo({ address: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={isEnglish ? 'Street, number, neighborhood, city, state, postal code' : 'Calle, número, colonia, ciudad, estado, código postal'}
                  />
                </div>

                {/* Proof of Address Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isEnglish ? 'Proof of Address' : 'Comprobante de Domicilio'}
                  </label>
                  <div
                    onClick={() => proofOfAddressRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                  >
                    <input
                      ref={proofOfAddressRef}
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'proofOfAddressFile')}
                    />
                    {formData.propertyInfo.proofOfAddressFile ? (
                      <div className="text-primary-600">
                        <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm">{formData.propertyInfo.proofOfAddressFile}</span>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-sm">{isEnglish ? 'Click to upload (optional)' : 'Clic para subir (opcional)'}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Near Sea */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Is the property less than 500 meters from the sea?' : '¿La propiedad está a menos de 500 metros del mar?'} *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ nearSea: true })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.nearSea === true
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">{isEnglish ? 'Yes' : 'Sí'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ nearSea: false })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.nearSea === false
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">No</span>
                    </button>
                  </div>
                </div>

                {/* Number of Floors */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isEnglish ? 'Number of Floors' : 'Número de Pisos'} *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.propertyInfo.numberOfFloors || ''}
                    onChange={(e) => updatePropertyInfo({ numberOfFloors: parseInt(e.target.value) || null })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={isEnglish ? 'Enter number of floors' : 'Ingrese el número de pisos'}
                  />
                </div>

                {/* Number of Rooms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isEnglish ? 'Number of Rooms' : 'Número de Habitaciones'} *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.propertyInfo.numberOfRooms || ''}
                    onChange={(e) => updatePropertyInfo({ numberOfRooms: parseInt(e.target.value) || null })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={isEnglish ? 'Enter number of rooms' : 'Ingrese el número de habitaciones'}
                  />
                </div>

                {/* Has Basement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Does it have a basement?' : '¿Tiene sótano?'} *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ hasBasement: true })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.hasBasement === true
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">{isEnglish ? 'Yes' : 'Sí'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ hasBasement: false })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.hasBasement === false
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">No</span>
                    </button>
                  </div>
                </div>

                {/* Property Use */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Property Use' : 'Uso de la Propiedad'} *
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ propertyUse: 'personal' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.propertyUse === 'personal'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium text-sm">{isEnglish ? 'Personal' : 'Personal'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ propertyUse: 'rental' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.propertyUse === 'rental'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium text-sm">{isEnglish ? 'Rental' : 'Renta'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ propertyUse: 'business' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.propertyUse === 'business'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium text-sm">{isEnglish ? 'Business' : 'Negocio'}</span>
                    </button>
                  </div>
                </div>

                {/* Walls Concrete Blocks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Are the walls made of concrete blocks?' : '¿Las paredes son de blocks de concreto?'} *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ wallsConcreteBlocks: true, wallsDetails: '' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.wallsConcreteBlocks === true
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">{isEnglish ? 'Yes' : 'Sí'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ wallsConcreteBlocks: false })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.wallsConcreteBlocks === false
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">No</span>
                    </button>
                  </div>
                  {formData.propertyInfo.wallsConcreteBlocks === false && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Please specify wall materials:' : 'Por favor especifique los materiales de las paredes:'} *
                      </label>
                      <textarea
                        value={formData.propertyInfo.wallsDetails}
                        onChange={(e) => updatePropertyInfo({ wallsDetails: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={isEnglish ? 'Describe the wall materials...' : 'Describa los materiales de las paredes...'}
                      />
                    </div>
                  )}
                </div>

                {/* Ceilings Concrete */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Are the ceilings made of concrete?' : '¿Los techos son de concreto?'} *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ ceilingsConcrete: true, ceilingsDetails: '' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.ceilingsConcrete === true
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">{isEnglish ? 'Yes' : 'Sí'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePropertyInfo({ ceilingsConcrete: false })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.propertyInfo.ceilingsConcrete === false
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">No</span>
                    </button>
                  </div>
                  {formData.propertyInfo.ceilingsConcrete === false && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isEnglish ? 'Please specify ceiling materials:' : 'Por favor especifique los materiales del techo:'} *
                      </label>
                      <textarea
                        value={formData.propertyInfo.ceilingsDetails}
                        onChange={(e) => updatePropertyInfo({ ceilingsDetails: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={isEnglish ? 'Describe the ceiling materials...' : 'Describa los materiales del techo...'}
                      />
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-6 border-t">
                  <button
                    onClick={handlePrevious}
                    className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  >
                    <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {isEnglish ? 'Previous' : 'Anterior'}
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!isStep2Valid()}
                    className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isEnglish ? 'Continue' : 'Continuar'}
                    <svg className="w-5 h-5 inline-block ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Values and Coverage */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {isEnglish ? 'Values & Coverage' : 'Valores y Cobertura'}
                </h2>

                {/* Construction Square Meters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isEnglish ? 'Construction Square Meters' : 'Metros Cuadrados de Construcción'}
                    <span className="text-gray-400 ml-1">({isEnglish ? 'optional' : 'opcional'})</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.coverageInfo.constructionSquareMeters || ''}
                    onChange={(e) => updateCoverageInfo({ constructionSquareMeters: parseInt(e.target.value) || null })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={isEnglish ? 'Enter square meters' : 'Ingrese metros cuadrados'}
                  />
                </div>

                {/* Coverage Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Type of Coverage' : 'Tipo de Cobertura'} *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => updateCoverageInfo({ coverageType: 'contents' })}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        formData.coverageInfo.coverageType === 'contents'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <span className="font-medium">{isEnglish ? 'Contents Only' : 'Solo Contenidos'}</span>
                      <p className="text-xs text-gray-500 mt-1">
                        {isEnglish ? 'Furniture, appliances, belongings' : 'Muebles, electrodomésticos, pertenencias'}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCoverageInfo({ coverageType: 'building' })}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        formData.coverageInfo.coverageType === 'building'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-medium">{isEnglish ? 'Building Only' : 'Solo Edificio'}</span>
                      <p className="text-xs text-gray-500 mt-1">
                        {isEnglish ? 'Structure, walls, installations' : 'Estructura, paredes, instalaciones'}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCoverageInfo({ coverageType: 'both' })}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        formData.coverageInfo.coverageType === 'both'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <span className="font-medium">{isEnglish ? 'Both' : 'Ambos'}</span>
                      <p className="text-xs text-gray-500 mt-1">
                        {isEnglish ? 'Complete coverage' : 'Cobertura completa'}
                      </p>
                    </button>
                  </div>
                </div>

                {/* Currency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isEnglish ? 'Currency for Values' : 'Moneda para los Valores'} *
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => updateCoverageInfo({ currency: 'MXN' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.coverageInfo.currency === 'MXN'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">MXN</span>
                      <p className="text-xs text-gray-500">{isEnglish ? 'Mexican Peso' : 'Peso Mexicano'}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCoverageInfo({ currency: 'USD' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.coverageInfo.currency === 'USD'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">USD</span>
                      <p className="text-xs text-gray-500">{isEnglish ? 'US Dollar' : 'Dólar Americano'}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCoverageInfo({ currency: 'EUR' })}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.coverageInfo.currency === 'EUR'
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">EUR</span>
                      <p className="text-xs text-gray-500">Euro</p>
                    </button>
                  </div>
                </div>

                {/* Placeholder for additional Step 3 fields */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="text-gray-600 text-center">
                    {isEnglish
                      ? 'Additional coverage options and values will be configured here.'
                      : 'Las opciones adicionales de cobertura y valores se configurarán aquí.'}
                  </p>
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-6 border-t">
                  <button
                    onClick={handlePrevious}
                    className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  >
                    <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {isEnglish ? 'Previous' : 'Anterior'}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!isStep3Valid()}
                    className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isEnglish ? 'Submit Application' : 'Enviar Solicitud'}
                    <svg className="w-5 h-5 inline-block ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Auto-save indicator */}
          <div className="mt-4 text-center text-sm text-gray-500">
            <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {isEnglish ? 'Your progress is automatically saved' : 'Su progreso se guarda automáticamente'}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomeInsuranceFormPage;
